import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import './SupportStatistics.css';

const SupportStatistics = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('7d');

  // 生成模拟统计数据
  const generateMockStats = () => {
    return {
      overview: {
        total_tickets: 1248,
        resolved_tickets: 1156,
        pending_tickets: 92,
        avg_response_time: 2.3,
        satisfaction_rate: 4.7,
        resolution_rate: 92.6
      },
      daily_stats: [
        { date: '2025-01-15', tickets: 45, resolved: 42, response_time: 2.1 },
        { date: '2025-01-14', tickets: 38, resolved: 35, response_time: 2.5 },
        { date: '2025-01-13', tickets: 52, resolved: 48, response_time: 2.0 },
        { date: '2025-01-12', tickets: 41, resolved: 39, response_time: 2.8 },
        { date: '2025-01-11', tickets: 36, resolved: 33, response_time: 2.2 },
        { date: '2025-01-10', tickets: 44, resolved: 41, response_time: 2.4 },
        { date: '2025-01-09', tickets: 39, resolved: 36, response_time: 2.6 }
      ],
      category_stats: [
        { category: '技术问题', count: 456, percentage: 36.5 },
        { category: '账户问题', count: 234, percentage: 18.8 },
        { category: '支付问题', count: 189, percentage: 15.1 },
        { category: '功能建议', count: 156, percentage: 12.5 },
        { category: '问题反馈', count: 123, percentage: 9.9 },
        { category: '其他', count: 90, percentage: 7.2 }
      ],
      agent_stats: [
        { name: '客服小王', tickets: 156, resolved: 148, satisfaction: 4.8, response_time: 1.9 },
        { name: '客服小李', tickets: 134, resolved: 128, satisfaction: 4.6, response_time: 2.1 },
        { name: '技术小王', tickets: 98, resolved: 92, satisfaction: 4.7, response_time: 2.3 },
        { name: '产品经理', tickets: 87, resolved: 82, satisfaction: 4.9, response_time: 2.0 }
      ],
      satisfaction_distribution: [
        { rating: 5, count: 456, percentage: 39.4 },
        { rating: 4, count: 234, percentage: 20.2 },
        { rating: 3, count: 123, percentage: 10.6 },
        { rating: 2, count: 45, percentage: 3.9 },
        { rating: 1, count: 23, percentage: 2.0 }
      ]
    };
  };

  const loadStatistics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // 使用模拟数据
      const mockStats = generateMockStats();
      setStats(mockStats);
      
      // 模拟API调用延迟
      await new Promise(resolve => setTimeout(resolve, 500));
      
    } catch (err) {
      console.error('Error loading statistics:', err);
      setError('加载统计数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatistics();
  }, [timeRange, loadStatistics]);

  const formatNumber = (num) => {
    return num.toLocaleString();
  };

  const formatPercentage = (num) => {
    return `${num.toFixed(1)}%`;
  };

  const formatTime = (hours) => {
    if (hours < 1) {
      return `${Math.round(hours * 60)}分钟`;
    }
    return `${hours.toFixed(1)}小时`;
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>
          <LoadingText>加载中...</LoadingText>
        </LoadingContainer>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <ErrorMessage>{error}</ErrorMessage>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>客服统计</PageTitle>
        <HeaderActions>
          <TimeRangeSelect 
            value={timeRange} 
            onChange={(e) => setTimeRange(e.target.value)}
          >
            <option value="7d">最近7天</option>
            <option value="30d">最近30天</option>
            <option value="90d">最近90天</option>
          </TimeRangeSelect>
          <RefreshButton onClick={loadStatistics}>
            刷新
          </RefreshButton>
        </HeaderActions>
      </PageHeader>

      {/* 概览统计 */}
      <OverviewSection>
        <StatCard>
          <StatContent>
            <StatValue>{formatNumber(stats.overview.total_tickets)}</StatValue>
            <StatLabel>总工单数</StatLabel>
          </StatContent>
        </StatCard>
        
        <StatCard>
          <StatContent>
            <StatValue>{formatNumber(stats.overview.resolved_tickets)}</StatValue>
            <StatLabel>已解决</StatLabel>
          </StatContent>
        </StatCard>
        
        <StatCard highlight>
          <StatContent>
            <StatValue>{formatNumber(stats.overview.pending_tickets)}</StatValue>
            <StatLabel>待处理</StatLabel>
          </StatContent>
        </StatCard>
        
        <StatCard>
          <StatContent>
            <StatValue>{formatTime(stats.overview.avg_response_time)}</StatValue>
            <StatLabel>平均响应时间</StatLabel>
          </StatContent>
        </StatCard>
        
        <StatCard>
          <StatContent>
            <StatValue>{stats.overview.satisfaction_rate}/5.0</StatValue>
            <StatLabel>满意度评分</StatLabel>
          </StatContent>
        </StatCard>
        
        <StatCard>
          <StatContent>
            <StatValue>{formatPercentage(stats.overview.resolution_rate)}</StatValue>
            <StatLabel>解决率</StatLabel>
          </StatContent>
        </StatCard>
      </OverviewSection>

      {/* 图表区域 */}
      <ChartsSection>
        <ChartCard>
          <ChartTitle>工单分类分布</ChartTitle>
          <PieChartWrapper>
            <PieChartContainer>
              <PieChart3D>
                <PieChartShadow />
                <PieChartMain>
                  {stats.category_stats.map((item, index) => {
                    const colors = ['#e53e3e', '#fbbf24', '#48bb78', '#38b2ac', '#3182ce', '#9f7aea'];
                    const startAngle = stats.category_stats.slice(0, index).reduce((sum, prev) => sum + prev.percentage, 0) * 3.6;
                    const endAngle = startAngle + item.percentage * 3.6;
                    return (
                      <PieSegment3D 
                        key={index}
                        startAngle={startAngle}
                        endAngle={endAngle}
                        color={colors[index]}
                        percentage={item.percentage}
                      />
                    );
                  })}
                </PieChartMain>
              </PieChart3D>
              <PieCenter>
                <PieCenterText>总计</PieCenterText>
                <PieCenterValue>{stats.category_stats.reduce((sum, item) => sum + item.count, 0)}</PieCenterValue>
              </PieCenter>
            </PieChartContainer>
            <PieLabels>
              {stats.category_stats.map((item, index) => {
                const colors = ['#e53e3e', '#fbbf24', '#48bb78', '#38b2ac', '#3182ce', '#9f7aea'];
                return (
                  <PieLabel key={index}>
                    <PieLabelColor color={colors[index]} />
                    <PieLabelText>{item.category}</PieLabelText>
                    <PieLabelValue>{item.count}</PieLabelValue>
                    <PieLabelPercentage>({item.percentage.toFixed(1)}%)</PieLabelPercentage>
                  </PieLabel>
                );
              })}
            </PieLabels>
          </PieChartWrapper>
        </ChartCard>

        <ChartCard>
          <ChartTitle>每日处理工单数</ChartTitle>
          <DailyChartWrapper>
            <DailyChartWithAxis>
              <DailyYAxis>
                <DailyYLabel>60</DailyYLabel>
                <DailyYLabel>45</DailyYLabel>
                <DailyYLabel>30</DailyYLabel>
                <DailyYLabel>15</DailyYLabel>
                <DailyYLabel>0</DailyYLabel>
              </DailyYAxis>
              <DailyBarsContainer>
                {stats.daily_stats.map((day, index) => {
                  const maxTickets = 60; // 固定最大值为60
                  const heightPercent = (day.tickets / maxTickets) * 100;
                  return (
                    <DailyBarItem key={index}>
                      <DailyBarValue>{day.tickets}</DailyBarValue>
                      <DailyBarColumn height={heightPercent}>
                        <DailyBarFill />
                      </DailyBarColumn>
                      <DailyBarDate>{day.date.substring(5)}</DailyBarDate>
                    </DailyBarItem>
                  );
                })}
              </DailyBarsContainer>
            </DailyChartWithAxis>
          </DailyChartWrapper>
        </ChartCard>
      </ChartsSection>

      {/* 详细统计表格 */}
      <TablesSection>
        <TableCard>
          <TableTitle>客服人员统计</TableTitle>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>客服人员</TableHeaderCell>
                <TableHeaderCell>处理工单</TableHeaderCell>
                <TableHeaderCell>已解决</TableHeaderCell>
                <TableHeaderCell>满意度</TableHeaderCell>
                <TableHeaderCell>平均响应时间</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stats.agent_stats.map((agent, index) => (
                <TableRow key={index}>
                  <TableCell>{agent.name}</TableCell>
                  <TableCell>{agent.tickets}</TableCell>
                  <TableCell>{agent.resolved}</TableCell>
                  <TableCell>
                    <SatisfactionScore>{agent.satisfaction}/5.0</SatisfactionScore>
                  </TableCell>
                  <TableCell>{formatTime(agent.response_time)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableCard>

        <TableCard>
          <TableTitle>满意度分布</TableTitle>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>评分</TableHeaderCell>
                <TableHeaderCell>数量</TableHeaderCell>
                <TableHeaderCell>占比</TableHeaderCell>
                <TableHeaderCell>进度条</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stats.satisfaction_distribution.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <RatingStars rating={item.rating} />
                  </TableCell>
                  <TableCell>{item.count}</TableCell>
                  <TableCell>{formatPercentage(item.percentage)}</TableCell>
                  <TableCell>
                    <ProgressBar>
                      <ProgressFill width={item.percentage} />
                    </ProgressBar>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableCard>
      </TablesSection>
    </PageContainer>
  );
};

export default SupportStatistics;

// 样式组件
const PageContainer = styled.div`
  padding: 24px;
  background: #f8fafc;
  min-height: 100vh;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
`;

const TimeRangeSelect = styled.select`
  padding: 8px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;
`;

const RefreshButton = styled.button`
  padding: 8px 16px;
  background: white;
  color: #667eea;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f8fafc;
    border-color: #667eea;
  }
`;

const OverviewSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid ${props => props.highlight ? '#f56565' : '#e2e8f0'};
  text-align: center;
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: #718096;
  font-weight: 500;
`;

const ChartsSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 32px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
  
  @media (max-width: 768px) {
    gap: 16px;
  }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const ChartTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 20px 0;
`;

const SimpleChart = styled.div`
  min-height: 300px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: visible;
`;

const ChartBars = styled.div`
  display: flex;
  align-items: end;
  gap: 8px;
  height: 200px;
  margin-bottom: 20px;
  position: relative;
`;

const ChartContainer = styled.div`
  display: flex;
  align-items: end;
  height: 200px;
  margin-bottom: 20px;
`;

const YAxis = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  margin-right: 12px;
  padding: 0 8px;
`;

const YAxisLabel = styled.div`
  font-size: 12px;
  color: #718096;
  text-align: right;
  line-height: 1;
`;

const ChartArea = styled.div`
  flex: 1;
  position: relative;
  height: 100%;
`;

const XAxisLine = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: #e2e8f0;
`;

const BarValueText = styled.div`
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 11px;
  color: #4a5568;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.9);
  padding: 2px 4px;
  border-radius: 3px;
  white-space: nowrap;
`;

const ChartBar = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  position: relative;
`;

const BarValue = styled.div`
  width: 100%;
  background: linear-gradient(to top, #667eea, #764ba2);
  border-radius: 4px 4px 0 0;
  height: ${props => props.height}%;
  min-height: 4px;
  margin-bottom: 8px;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    background: linear-gradient(to top, #5a67d8, #6b46c1);
    transform: scaleY(1.05);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }
`;

const BarLabel = styled.div`
  font-size: 12px;
  color: #718096;
`;

const ChartLegend = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LegendColor = styled.div`
  width: 12px;
  height: 12px;
  background: ${props => props.color};
  border-radius: 2px;
`;

const LegendText = styled.div`
  font-size: 14px;
  color: #4a5568;
`;

// 每日工单数图表样式组件
const DailyChartWrapper = styled.div`
  padding: 10px 0;
  min-height: 220px;
`;

const DailyChartWithAxis = styled.div`
  display: flex;
  gap: 12px;
  align-items: stretch;
`;

const DailyYAxis = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 180px;
  padding-top: 20px;
  padding-bottom: 32px;
`;

const DailyYLabel = styled.div`
  font-size: 11px;
  color: #718096;
  text-align: right;
  min-width: 25px;
  line-height: 1;
`;

const DailyBarsContainer = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-around;
  height: 180px;
  gap: 12px;
  flex: 1;
  padding: 20px 10px 32px 0;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    bottom: 32px;
    left: 0;
    right: 10px;
    height: 1px;
    background: #e2e8f0;
  }
`;

const DailyBarItem = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  height: 160px;
  max-width: 80px;
  position: relative;
`;

const DailyBarValue = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 4px;
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
`;

const DailyBarColumn = styled.div`
  width: 100%;
  height: ${props => Math.max(props.height, 3)}%;
  min-height: 3px;
  display: flex;
  align-items: flex-end;
  position: relative;
`;

const DailyBarFill = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, #667eea 0%, #764ba2 100%);
  border-radius: 6px 6px 0 0;
  transition: all 0.3s ease;
  box-shadow: 0 -2px 8px rgba(102, 126, 234, 0.3);
  
  &:hover {
    background: linear-gradient(180deg, #5a67d8 0%, #6b46c1 100%);
    box-shadow: 0 -4px 12px rgba(102, 126, 234, 0.4);
    transform: scaleY(1.02);
  }
`;

const DailyBarDate = styled.div`
  font-size: 11px;
  color: #718096;
  margin-top: 6px;
  text-align: center;
  position: absolute;
  bottom: -26px;
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
`;

const PieChartWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  min-height: 220px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 16px;
    min-height: auto;
  }
`;

const PieChartContainer = styled.div`
  position: relative;
  width: 200px;
  height: 200px;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 160px;
    height: 160px;
  }
`;

const PieChart3D = styled.div`
  position: relative;
  width: 200px;
  height: 200px;
`;

const PieChartShadow = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.1);
  z-index: 1;
`;

const PieChartMain = styled.div`
  position: relative;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: conic-gradient(
    #e53e3e 0deg 36.5%,
    #fbbf24 36.5% 55.3%,
    #48bb78 55.3% 70.4%,
    #38b2ac 70.4% 82.9%,
    #3182ce 82.9% 92.8%,
    #9f7aea 92.8% 100%
  );
  z-index: 2;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const PieSegment3D = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: ${props => props.color};
  clip-path: polygon(
    50% 50%, 
    ${props => 50 + 50 * Math.cos((props.startAngle - 90) * Math.PI / 180)}% ${props => 50 + 50 * Math.sin((props.startAngle - 90) * Math.PI / 180)}%,
    ${props => 50 + 50 * Math.cos((props.endAngle - 90) * Math.PI / 180)}% ${props => 50 + 50 * Math.sin((props.endAngle - 90) * Math.PI / 180)}%
  );
  z-index: 3;
`;

const PieCenter = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  background: white;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 4;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const PieCenterText = styled.div`
  font-size: 12px;
  color: #718096;
  margin-bottom: 2px;
`;

const PieCenterValue = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #2d3748;
`;

const PieLabelPercentage = styled.div`
  font-size: 12px;
  color: #a0aec0;
  margin-left: 4px;
`;

const PieLabels = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  max-height: 200px;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    max-height: none;
    overflow-y: visible;
  }
`;

const PieLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 6px;
  transition: all 0.2s ease;
  cursor: pointer;
  font-size: 13px;
  
  &:hover {
    background: #f8fafc;
    transform: translateX(2px);
  }
`;

const PieLabelColor = styled.div`
  width: 12px;
  height: 12px;
  background: ${props => props.color};
  border-radius: 2px;
`;

const PieLabelText = styled.div`
  font-size: 13px;
  color: #4a5568;
  flex: 1;
`;

const PieLabelValue = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #2d3748;
`;

const TablesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const TableCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const TableTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 20px 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: #f8fafc;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #e2e8f0;
  
  &:hover {
    background: #f8fafc;
  }
`;

const TableHeaderCell = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const TableBody = styled.tbody``;

const TableCell = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #4a5568;
`;

const SatisfactionScore = styled.span`
  color: #48bb78;
  font-weight: 600;
`;

const RatingStars = styled.div`
  display: flex;
  gap: 2px;
  
  &::before {
    content: '★★★★★';
    color: #e2e8f0;
    font-size: 16px;
  }
  
  &::after {
    content: '★★★★★';
    color: #fbbf24;
    font-size: 16px;
    position: absolute;
    width: ${props => (props.rating / 5) * 100}%;
    overflow: hidden;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #667eea, #764ba2);
  width: ${props => props.width}%;
  transition: width 0.3s ease;
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 60px 20px;
`;

const LoadingText = styled.div`
  color: #718096;
  font-size: 14px;
`;

const ErrorMessage = styled.div`
  background: #fff5f5;
  color: #c53030;
  padding: 16px 20px;
  border-radius: 8px;
  border: 1px solid #feb2b2;
  font-size: 14px;
  margin-bottom: 20px;
`;
